import type { ReactNode } from 'react';
import { random } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { addSet, addVariant } from '../../utils/cart';
import { checkIfItemCanBeSoldOut, convertDateToISOFormat, heapTrack, isTagPresent } from '../../utils/helpers';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { IPdpData, IVariant, TUserSelections } from './types/PDP';
import { computeForcedMatchingSetsLineItemProperties } from './variations/forcedMatchingSets';
import { dispatchOpenVariantSelectorDropdownEvent } from './components/VariantSelections/utils';
import { getProductsDataByColorSelection } from './components/product-utils';
import { NonEmptyArray, notEmpty, notNil } from 'src/scripts/utils/typescriptUtils';
import { extractProductIdFromDecodedGraphQLId } from 'src/scripts/utils/shopify';
import { PREORDER_TAG } from '../../utils/constants';
import {
  emailRegex,
  GiftCardRecipientFields,
  GiftCardRecipientType,
  GiftCardSendDateType,
  noWhitespaceRegex,
} from './components/VariantSelections/GiftCardRecipientUI';

const defaultValues: IDefaultValues = {
  addToCart: () => Promise.resolve(false),
  openOptionDropdown: () => {
    dispatchOpenVariantSelectorDropdownEvent();
  },
  purchaseInProgress: false,
  isSelectionError: false,
  isSizeGuideOpen: false,
  isAccordionCollapsed: false,
  similarItemsCarouselExists: false,
  changeIsModalOpenByATCbutton: () => {},
  toggleSelectionError: () => {},
  toggleSizeGuide: () => {},
  setFitAndDescriptionView: () => {},
  assignProductData: () => {},
  setSimilarItemsCarouselExists: () => {},
  giftCardRecipientType: 'someoneElse',
  giftCardSendDateType: 'now',
  giftCardRecipientFields: {
    name: '',
    email: '',
    message: '',
    delivery_date: undefined,
  },
  areGiftCardRecipientFieldsValid: false,
  setGiftCardRecipientType: () => {},
  setGiftCardSendDateType: () => {},
  updateGiftCardRecipientFields: () => {},
  addToCartCount: 0,
};

export const ProductContext = React.createContext(defaultValues);

// ? [REVISIT] has a mix or local & "context" level concerns
const ProductProvider = ({ children }: { children: ReactNode }) => {
  const [productData, setProductData] = useState<IProductContextData>();
  const [isModalOpenByATCbutton, setIsModalOpenByATCbutton] = useState(false);
  const [isSelectionError, setSelectionError] = useState(false);
  const [isSizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [isAccordionCollapsed, setAccordionCollapsed] = useState(false);
  // ATC button Animations
  const [purchaseInProgress, setPurchaseInProgress] = useState(false);
  const [similarItemsCarouselExists, setSimilarItemsCarouselExists] = useState(false);
  const [giftCardRecipientType, setGiftCardRecipientType] = useState<GiftCardRecipientType>('someoneElse');
  const [giftCardSendDateType, setGiftCardSendDateType] = useState<GiftCardSendDateType>('now');
  const [addToCartCount, setAddToCartCount] = useState<number>(0);
  const [giftCardRecipientFields, setGiftCardRecipientFields] = useState<GiftCardRecipientFields>({
    name: '',
    email: '',
    message: '',
    delivery_date: undefined,
  });

  useEffect(() => {
    if (productData?.userSelections[0]?.Size && isModalOpenByATCbutton) {
      addToCart();
    }
    changeIsModalOpenByATCbutton(false);
    toggleSelectionError(false);
    return () => {};
    // ! [REVISIT]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productData?.userSelections]);

  const assignProductData = (newValue: IProductContextData) => {
    setProductData(newValue);
  };

  const changeIsModalOpenByATCbutton = (newValue: boolean) => {
    setIsModalOpenByATCbutton(newValue);
  };
  const toggleSelectionError = (newValue: boolean) => {
    setSelectionError(newValue);
  };
  const toggleSizeGuide = (newValue: boolean) => {
    setSizeGuideOpen(newValue);
  };

  const setFitAndDescriptionView = (newValue: boolean) => {
    setAccordionCollapsed(newValue);
  };

  const updateGiftCardRecipientFields = (fields: Partial<GiftCardRecipientFields>) => {
    setGiftCardRecipientFields((prevFields) => ({ ...prevFields, ...fields }));
  };

  const incrementAddToCartCount = () => setAddToCartCount((prevValue) => prevValue + 1);

  const resetGiftCardRecipientFields = () => {
    setGiftCardRecipientFields({
      name: '',
      email: '',
      message: '',
      delivery_date: undefined,
    });
    setGiftCardSendDateType('now');
  };

  const areGiftCardRecipientFieldsValid = useMemo(() => {
    const emailRegExp = new RegExp(emailRegex);
    const emailIsValid = emailRegExp.test(giftCardRecipientFields.email);
    const nameRegex = new RegExp(noWhitespaceRegex);
    const nameIsValid = nameRegex.test(giftCardRecipientFields.name);
    return nameIsValid && emailIsValid;
  }, [giftCardRecipientFields]);

  const addToCart = async (ATBtype = 'Standard') => {
    let didSucceed = false;
    const { selectedVariants, pdpData, userSelections } = productData || {};

    const isPreorderProduct = isTagPresent(productData?.currentTags || [], PREORDER_TAG);
    const preorderEstimatedShipDate = selectedVariants?.[0]?.estimatedShipDate?.value;
    const preoderLineItemProps =
      isPreorderProduct && preorderEstimatedShipDate
        ? {
            _preorder_estimated_ship_date: preorderEstimatedShipDate,
          }
        : {};

    const { email, name, message, delivery_date } = giftCardRecipientFields;
    const giftCardRecipientProps =
      giftCardRecipientType === 'someoneElse' && areGiftCardRecipientFieldsValid
        ? {
            'Recipient email': email,
            'Recipient name': name,
            Message: message,
            'Send on':
              giftCardSendDateType === 'later' && delivery_date ? convertDateToISOFormat(delivery_date) : undefined,
            __shopify_send_gift_card_to_recipient: true,
          }
        : {};

    if (!(selectedVariants && pdpData && userSelections)) return false;
    const variants = selectedVariants.filter((variant) => variant?.availableForSale).filter(notNil);
    const colorwayProductData = getProductsDataByColorSelection(pdpData, userSelections).filter(notNil);

    if (variants.length === colorwayProductData.length && notEmpty(variants) && notEmpty(colorwayProductData)) {
      const ids = variants.map((item) => item.id) as NonEmptyArray<string>;
      const variantPricesData = variants.map((item, index) => {
        const tags = colorwayProductData[index]?.tags ?? [];
        const productId = colorwayProductData[0].id;
        return {
          id: Number(item.id),
          price: item.price,
          displayPrice: item.displayPrice,
          tags,
          productId: Number(extractProductIdFromDecodedGraphQLId(productId)),
          quantityAvailable: checkIfItemCanBeSoldOut(tags) ? item.quantityAvailable : 9999, // ? [REVISIT] Detect non-inventory-tracked items better. We just don't have the "is a gc" field or "is inventory tracked"
        };
      });
      try {
        // need to update ts version — already notEmpty check above
        if (!notEmpty(variantPricesData)) throw new Error('Empty variant prices data');
        setPurchaseInProgress(true);
        const promise = pdpData.isASet
          ? // ? [REVISIT] (optimally) it should NOT be difficult/require this many params to add a variant
            addSet(
              ids,
              variantPricesData,
              {
                _setName: pdpData.pdpProductShell.title,
                _setUrl: `/products/${pdpData.pdpProductShell.handle}`,
                _setUid: `${random(1, 100000000)}`,
                ...computeForcedMatchingSetsLineItemProperties(pdpData, userSelections),
                ...preoderLineItemProps,
              },
              ATBtype
            )
          : addVariant(ids[0], variantPricesData[0], { ATBtype }, undefined, {
              ...preoderLineItemProps,
              ...giftCardRecipientProps,
            }).then((data) => {
              if (data?.error) throw data.error;
            });
        await promise;
        didSucceed = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        didSucceed = false;
        heapTrack('pdp add to bag failed', {
          err: err?.message ?? err,
          href: window.location.href,
          variantIds: ids.join(','),
          handle: pdpData.pdpProductShell.handle,
          title: pdpData.pdpProductShell.title,
        });
      } finally {
        setPurchaseInProgress(false);
      }
    } else {
      console.error(
        'You should supply productData to context first. Please see/use assignProductData() from ProductContext'
      );
    }
    if (didSucceed) {
      runPdpAddedToCartSideEffects();
      incrementAddToCartCount();
      resetGiftCardRecipientFields();
    }
    return didSucceed;
  };

  return (
    <ProductContext.Provider
      value={{
        ...defaultValues,
        purchaseInProgress,
        isSelectionError,
        isSizeGuideOpen,
        isAccordionCollapsed,
        similarItemsCarouselExists,
        changeIsModalOpenByATCbutton,
        toggleSelectionError,
        toggleSizeGuide,
        setFitAndDescriptionView,
        addToCart,
        assignProductData,
        setSimilarItemsCarouselExists,
        giftCardRecipientType,
        giftCardSendDateType,
        giftCardRecipientFields,
        areGiftCardRecipientFieldsValid,
        setGiftCardRecipientType,
        setGiftCardSendDateType,
        updateGiftCardRecipientFields,
        addToCartCount,
      }}
    >
      {children}
    </ProductContext.Provider>
  );
};

export default ProductProvider;

// ? arguably this chould listen for the ADDED_TO_CART event & fire based on that
const runPdpAddedToCartSideEffects = () => {
  // for anlaytics tools that need to listen for click events (optimizely)
  const selector = `[data-analytics-id='added-to-cart-button']`;
  document.querySelector(selector)?.click();
};

interface IDefaultValues {
  addToCart: (ATBtype: string) => Promise<boolean | undefined>;
  openOptionDropdown: () => void;
  purchaseInProgress: boolean;
  isSelectionError: boolean;
  isSizeGuideOpen: boolean;
  isAccordionCollapsed: boolean;
  similarItemsCarouselExists: boolean;
  changeIsModalOpenByATCbutton: (newValue: boolean) => void;
  toggleSelectionError: (newValue: boolean) => void;
  toggleSizeGuide: (newValue: boolean) => void;
  setFitAndDescriptionView: (newValue: boolean) => void;
  assignProductData: (newValue: IProductContextData) => void;
  setSimilarItemsCarouselExists: (newValue: boolean) => void;
  giftCardRecipientType: GiftCardRecipientType;
  giftCardSendDateType: GiftCardSendDateType;
  giftCardRecipientFields: GiftCardRecipientFields;
  areGiftCardRecipientFieldsValid: boolean;
  setGiftCardRecipientType: (newValue: GiftCardRecipientType) => void;
  setGiftCardSendDateType: (newValue: GiftCardSendDateType) => void;
  updateGiftCardRecipientFields: (fields: Partial<GiftCardRecipientFields>) => void;
  addToCartCount: number;
}

interface IProductContextData {
  selectedVariants: (IVariant | undefined)[];
  currentTags: string[];
  pdpData: IPdpData;
  userSelections: TUserSelections;
}
